<template>
  <ion-page>
    <div class="youtube-container">
      <iframe
        v-if="embedUrl"
        id="player"
        :src="embedUrl"
        allowfullscreen
        allow="
          accelerometer;
          autoplay;
          clipboard-write;
          encrypted-media;
          gyroscope;
          picture-in-picture;
          web-share;
        "
        referrerpolicy="strict-origin-when-cross-origin"
      ></iframe>
      <div v-else class="error">
        <div>
          <strong>Error: Missing or invalid video ID</strong>
        </div>
        <div class="error-message">
          Please provide a valid YouTube video ID or URL using:<br />?v=VIDEO_ID<br />or<br />?url=https://youtube.com/watch?v=VIDEO_ID
        </div>
      </div>
    </div>
  </ion-page>
</template>

<script>
import { IonPage } from '@ionic/vue';

export default {
  name: 'YoutubeProxy',
  components: {
    IonPage,
  },
  data() {
    return {
      embedUrl: null,
      videoId: null,
      player: null,
      timeUpdateInterval: null,
      messageListener: null,
    };
  },
  computed: {
    targetOrigin() {
      return process.env.VUE_APP_APP_DOMAIN || window.location.origin;
    },
  },
  mounted() {
    this.initYouTubePlayer();
    this.setupMessageListener();
  },
  beforeUnmount() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    if (this.player) {
      this.player.destroy();
    }
  },
  methods: {
    extractVideoId(url) {
      if (!url) return null;

      if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
      }

      try {
        if (url.includes('youtube.com/watch')) {
          return new URL(url).searchParams.get('v');
        }
        if (url.includes('youtu.be/')) {
          const parts = url.split('youtu.be/')[1];
          return parts?.split('?')[0]?.split('&')[0];
        }
        if (url.includes('/embed/')) {
          const match = url.match(/\/embed\/([^?&#]+)/);
          return match?.[1];
        }
        if (url.includes('v=')) {
          const match = url.match(/[?&]v=([^?&#]+)/);
          return match?.[1];
        }
      } catch (e) {
        return null;
      }

      return null;
    },
    initYouTubePlayer() {
      const urlParams = new URLSearchParams(window.location.search);
      const videoId = this.getVideoIdFromParams(urlParams);

      if (!this.isValidVideoId(videoId)) {
        this.embedUrl = null;
        return;
      }

      this.videoId = videoId;
      this.embedUrl = this.buildEmbedUrl(videoId, urlParams);
      this.loadYouTubeAPI();
    },
    getVideoIdFromParams(urlParams) {
      let videoId = urlParams.get('v');
      const videoUrl = urlParams.get('url');

      if (videoUrl) {
        const extractedId = this.extractVideoId(videoUrl);
        if (extractedId) videoId = extractedId;
      }

      if (!videoId) {
        videoId = this.extractVideoId(window.location.href);
      }

      return videoId;
    },
    isValidVideoId(videoId) {
      return videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId);
    },
    buildEmbedUrl(videoId, urlParams) {
      const embedParams = new URLSearchParams({
        autoplay: urlParams.get('autoplay') || '0',
        controls: urlParams.get('controls') || '1',
        rel: urlParams.get('rel') || '0',
        modestbranding: urlParams.get('modestbranding') || '1',
        playsinline: urlParams.get('playsinline') || '1',
        enablejsapi: '1',
        origin: window.location.origin,
      });

      this.addOptionalParams(embedParams, urlParams, videoId);

      return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
        videoId,
      )}?${embedParams.toString()}`;
    },
    addOptionalParams(embedParams, urlParams, videoId) {
      const loop = urlParams.get('loop');
      if (loop === '1') {
        embedParams.append('loop', '1');
        embedParams.append('playlist', urlParams.get('playlist') || videoId);
      }

      const mute = urlParams.get('mute');
      if (mute === '1') {
        embedParams.append('mute', '1');
      }

      const start = urlParams.get('start');
      if (start) {
        embedParams.append('start', start);
      }

      const end = urlParams.get('end');
      if (end) {
        embedParams.append('end', end);
      }
    },
    loadYouTubeAPI() {
      if (window.YT?.Player) {
        this.initializePlayer();
        return;
      }

      this.ensureYouTubeScriptLoaded();
      this.registerAPIReadyCallback();
      this.scheduleFallbackInit();
    },
    ensureYouTubeScriptLoaded() {
      const scriptSrc = 'https://www.youtube.com/iframe_api';
      const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

      if (!existingScript) {
        const tag = document.createElement('script');
        tag.src = scriptSrc;
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    },
    registerAPIReadyCallback() {
      if (!window.youtubeAPIReadyCallbacks) {
        window.youtubeAPIReadyCallbacks = [];
      }

      window.youtubeAPIReadyCallbacks.push(() => {
        this.initializePlayer();
      });

      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          window.youtubeAPIReadyCallbacks?.forEach(callback => callback());
          window.youtubeAPIReadyCallbacks = [];
        };
      }
    },
    scheduleFallbackInit() {
      setTimeout(() => {
        if (window.YT?.Player && !this.player) {
          this.initializePlayer();
        }
      }, 1000);
    },
    initializePlayer() {
      const targetElement = document.getElementById('player');
      if (!targetElement || !this.videoId) return;

      this.$nextTick(() => {
        this.attachYouTubeAPI(targetElement);
      });
    },
    attachYouTubeAPI(iframe) {
      const attachAPI = () => {
        this.player = new window.YT.Player('player', {
          events: {
            onReady: this.onPlayerReady,
            onStateChange: this.onPlayerStateChange,
            onError: this.onPlayerError,
          },
        });
      };

      if (iframe.contentDocument?.readyState === 'complete') {
        attachAPI();
        return;
      }

      iframe.addEventListener('load', attachAPI, { once: true });

      setTimeout(() => {
        if (!this.player) {
          attachAPI();
        }
      }, 3000);
    },
    onPlayerReady() {
      // Start sending time updates periodically
      this.startTimeUpdateInterval();
    },
    onPlayerStateChange(event) {
      const state = event.data;
      const YTState = window.YT.PlayerState;

      if (state === YTState.PLAYING) {
        this.sendMessage('play');
        this.startTimeUpdateInterval();
      } else if (state === YTState.PAUSED) {
        this.sendMessage('pause');
        this.stopTimeUpdateInterval();
      } else if (state === YTState.ENDED) {
        this.sendMessage('ended');
        this.stopTimeUpdateInterval();
      }
    },
    startTimeUpdateInterval() {
      this.stopTimeUpdateInterval();

      this.timeUpdateInterval = setInterval(() => {
        const currentTime = this.player?.getCurrentTime?.();
        const duration = this.player?.getDuration?.();

        if (currentTime !== undefined && duration !== undefined) {
          this.sendMessage('timeupdate', { currentTime, duration });
        }
      }, 1000);
    },
    stopTimeUpdateInterval() {
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
        this.timeUpdateInterval = null;
      }
    },
    sendMessage(action, data = null) {
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'youtube-player',
            action,
            data,
          },
          this.targetOrigin,
        );
      }
    },
    setupMessageListener() {
      this.messageListener = event => {
        if (event.origin !== this.targetOrigin) return;
        if (event.data?.type !== 'youtube-control') return;
        if (!this.player) return;

        const { action, seconds } = event.data;

        switch (action) {
          case 'play':
            this.player.playVideo?.();
            break;
          case 'seek':
            if (seconds !== undefined) {
              this.player.seekTo?.(seconds, true);
            }
            break;
        }
      };

      window.addEventListener('message', this.messageListener);
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.youtube-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #000;
  z-index: 9999;
}

#player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.error {
  color: white;
  padding: 20px;
  text-align: center;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  flex-direction: column;
}

.error-message {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.8;
}
</style>
